import { Link } from 'react-router-dom';
import { Box, Chip, Tooltip } from '@mui/material';
import type { ParticipantRankRow, RankType } from '../api/contests';
import { rankChipColor, rankDisplayLabel, rankTitle, rankPriority, normalizeRankType } from '../utils/ranks';

const generateColorFromIdAndName = (participantId: number, name: string) => {
  let hash = participantId;
  const combinedName = `${participantId}-${name}`; // Combine participantId and name

  for (let i = 0; i < combinedName.length; i++) {
    hash = combinedName.charCodeAt(i) + ((hash << 5) - hash);
  }

  return `hsl(${hash % 360}, 70%, 80%)`; // Creates a soft color based on the hash
};

const getInitials = (name: string) => {
  const nameParts = name.trim().split(' ');
  return nameParts.length > 1
    ? nameParts[0][0].toUpperCase() + nameParts[1][0].toUpperCase()
    : nameParts[0][0].toUpperCase();
};

type RankCount = { type: RankType; count: number };

type RankLike = RankType | { rank_type?: RankType; contest_id?: number | string; result_group?: string | null };
// Alias for external callers that already have ParticipantRankRow objects.
export type RankLikeRow = ParticipantRankRow;

const getRankTypeAndContext = (r: RankLike) => {
  if (r && typeof r === 'object' && 'rank_type' in r) {
    return {
      type: normalizeRankType(r.rank_type as RankType),
      contestId: r.contest_id !== undefined && r.contest_id !== null ? String(r.contest_id) : '',
      group: r.result_group !== undefined && r.result_group !== null ? String(r.result_group) : '',
    };
  }
  return { type: normalizeRankType(r as RankType), contestId: '', group: '' };
};

const getRankCounts = (ranks?: RankLike[], maxCount: number = 3): RankCount[] => {
  if (!ranks?.length) return [];

  // In a single contest+group, a participant can satisfy multiple rank thresholds.
  // We want to count only the BEST rank per (contest_id, result_group).
  const bestByContext = new Map<string, { type: RankType; priority: number }>();
  const noContext: RankType[] = [];

  for (const r of ranks) {
    const ctx = getRankTypeAndContext(r);
    const t = ctx.type as RankType;
    if (!t) continue;

    // If we don't have contest/group context, keep legacy behavior (count as-is).
    if (!ctx.contestId) {
      noContext.push(t);
      continue;
    }

    const key = `${ctx.contestId}::${ctx.group}`;
    const p = rankPriority(t);
    const prev = bestByContext.get(key);
    if (!prev || p > prev.priority) {
      bestByContext.set(key, { type: t, priority: p });
    }
  }

  const map = new Map<string, number>();
  for (const { type } of bestByContext.values()) {
    map.set(type, (map.get(type) ?? 0) + 1);
  }
  for (const t of noContext) {
    map.set(t, (map.get(t) ?? 0) + 1);
  }

  const rows: RankCount[] = Array.from(map.entries()).map(([type, count]) => ({ type, count }));
  rows.sort((a, b) => {
    const pa = rankPriority(a.type);
    const pb = rankPriority(b.type);
    if (pa !== pb) return pb - pa; // higher first
    if (a.count !== b.count) return b.count - a.count;
    return String(a.type).localeCompare(String(b.type));
  });
  return rows.slice(0, maxCount);
};

export const RenderParticipantName = (
  participantId: number,
  name: string,
  profilePicture: string | null = null,
  noBorder: boolean = false,
  rankTypes: RankLike[] = []
) => {
  const topRanks = getRankCounts(rankTypes, 3);

  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...(noBorder ? {} : {}),
      }}
    >
      <Link
        to={`/participant/${participantId}`}
        style={{
          textDecoration: 'none',
          color: '#1976d2',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          transition: 'color 0.3s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#0d47a1')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#1976d2')}
      >
        {profilePicture ? (
          <img
            src={`https://photoskiff.com/zaruba/dbapi/uploads/${profilePicture}`}
            alt={name}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              marginRight: '10px',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: generateColorFromIdAndName(participantId, name),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: '#ffffff',
              fontWeight: 'bold',
              marginRight: '10px',
            }}
          >
            {getInitials(name)}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>{name}</span>
          {topRanks.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', marginTop: '2px' }}>
              {topRanks.map(({ type, count }) => {
                const title = rankTitle(type);
                const label = count > 1 ? `${rankDisplayLabel(type)}×${count}` : rankDisplayLabel(type);
                return (
                  <Tooltip key={`${String(type)}:${count}`} title={count > 1 ? `${title} (выполнено: ${count})` : title} arrow>
                    <Chip
                      label={label}
                      size="small"
                      variant="outlined"
                      color={rankChipColor(type)}
                      sx={{ height: 18, fontSize: '0.70rem', fontWeight: 700 }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Box>
      </Link>
    </Box>
  );
};

export const RenderParticipantPicture = (
  participantId: number,
  name: string,
  profilePicture: string | null = null,
  size: number = 40
) => {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
      <Link
        to={`/participant/${participantId}`}
        style={{
          textDecoration: 'none',
          color: '#1976d2',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          transition: 'color 0.3s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#0d47a1')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#1976d2')}
      >
        {profilePicture ? (
          <img
            src={`https://photoskiff.com/zaruba/dbapi/uploads/${profilePicture}`}
            alt={name}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              marginRight: '10px',
            }}
          />
        ) : (
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: generateColorFromIdAndName(participantId, name),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: '#ffffff',
              fontWeight: 'bold',
              marginRight: '10px',
            }}
          >
            {getInitials(name)}
          </Box>
        )}
      </Link>
    </Box>
  );
};
